import CompanyLookup from '../../../components/CompanyLookup';
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
  ModalClose,
} from "@mui/joy";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import {
  StudioModal,
  StudioDialog,
} from "../../../components/Workspace/Workspace";
import { documentRequest } from "../../../settings/documentSettingsApi";
import "./documents.css";
export const DOC_TYPES = [
  ["invoice", "Счёт"],
  ["contract", "Договор"],
  ["invoice_contract", "Счёт-договор"],
  ["act", "Акт"],
  ["upd", "УПД"],
  ["edo_agreement", "Соглашение ЭДО"],
];
export const CLIENT_TYPES = [
  ["individual", "Физическое лицо"],
  ["ip", "ИП"],
  ["ooo", "ООО"],
  ["self_employed", "Самозанятый"],
  ["ip_npd", "ИП на НПД"],
  ["legal", "Юридическое лицо"],
];
export const STATUS = {
  draft: ["Черновик", "neutral"],
  formed: ["Сформирован", "primary"],
  verified: ["Проверен", "warning"],
  sent: ["Отправлен", "success"],
  signed: ["Подписан", "success"],
  paid: ["Оплачен", "success"],
  cancelled: ["Аннулирован", "danger"],
};
export const money = (value) =>
  `${Number(value || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;
export const nameOf = (c) =>
  c?.display_name ||
  c?.company_name ||
  c?.full_name ||
  [c?.last_name, c?.name, c?.patronymic].filter(Boolean).join(" ") ||
  c?.phone ||
  "Заказчик не выбран";
export const listSlots = {
  listbox: { sx: { zIndex: 10140, maxHeight: 280, borderRadius: "12px" } },
};
export function Field({
  label,
  value,
  onChange,
  type = "text",
  hint,
  inputMode,
  required = false,
  ...rest
}) {
  return (
    <FormControl required={required}>
      <FormLabel>{label}</FormLabel>
      <Input
        type={type}
        slotProps={{ input: { inputMode } }}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        {...rest}
      />
      {hint && <FormHelperText>{hint}</FormHelperText>}
    </FormControl>
  );
}
export function Choice({ label, value, onChange, options, disabled = false }) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        aria-label={label}
        value={value ?? ""}
        onChange={(_, v) => v !== null && onChange(v)}
        disabled={disabled}
        slotProps={listSlots}
      >
        {options.map(([v, t]) => (
          <Option key={v} value={v}>
            {t}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
}
export function Section({ title, description, children, action }) {
  return (
    <Card className="doc-section">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        flexWrap="wrap"
      >
        <Box sx={{ minWidth: 0, flex: "1 1 180px" }}>
          <Typography level="title-lg" sx={{ overflowWrap: "anywhere" }}>
            {title}
          </Typography>
          {description && (
            <Typography
              level="body-sm"
              sx={{ mt: 0.5, color: "text.tertiary" }}
            >
              {description}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Card>
  );
}
export function DocumentDialog({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  footer,
  busy = false,
}) {
  return (
    <StudioModal open onClose={busy ? undefined : onClose}>
      <StudioDialog
        className="doc-dialog"
        sx={{ width: wide ? 900 : 640, p: 0, overflow: "hidden" }}
      >
        <header className="doc-dialog-head">
          <Typography level="h4">{title}</Typography>
          {subtitle && (
            <Typography
              level="body-sm"
              sx={{ color: "text.tertiary", mt: 0.5, pr: 3 }}
            >
              {subtitle}
            </Typography>
          )}
          <ModalClose aria-label="Закрыть" disabled={busy} />
        </header>
        <div className="doc-dialog-body">{children}</div>
        {footer && <footer className="doc-dialog-footer">{footer}</footer>}
      </StudioDialog>
    </StudioModal>
  );
}
export function SearchPicker({
  kind = "events",
  token,
  label,
  value,
  onSelect,
  onClear,
  clientId,
  initialQuery = "",
  exclude = [],
}) {
  const [query, setQuery] = useState(initialQuery),
    [items, setItems] = useState([]),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [open, setOpen] = useState(!value);
  useEffect(() => {
    if (!open) return;
    const ac = new AbortController();
    setLoading(true);
    setError("");
    const id = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ kind, q: query });
        if (clientId) p.set("client_id", clientId);
        const r = await documentRequest(`lookup/?${p}`, {
          token,
          signal: ac.signal,
        });
        setItems(r.results || []);
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 280);
    return () => {
      clearTimeout(id);
      ac.abort();
    };
  }, [query, kind, token, clientId, open]);
  return (
    <Stack gap={1}>
      <FormControl>
        <FormLabel>
          {label ||
            (kind === "events" ? "Найти объект или заказ" : "Найти заказчика")}
        </FormLabel>
        <Input
          aria-label={
            label || (kind === "events" ? "Поиск события" : "Поиск клиента")
          }
          startDecorator={<SearchRounded />}
          placeholder={
            kind === "events"
              ? "Адрес, сделка, событие, телефон, клиент…"
              : "ФИО, компания, телефон, email или ИНН…"
          }
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          endDecorator={loading ? <CircularProgress size="sm" /> : null}
        />
      </FormControl>
      {value && (
        <Card className="doc-selected">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography level="title-sm">
                {kind === "events"
                  ? value.address || `Заказ №${value.number}`
                  : nameOf(value)}
              </Typography>
              <Typography level="body-xs">
                {kind === "events"
                  ? `Сделка №${value.deal_id} · Событие №${value.id} · ${money(value.amount)}`
                  : value.phone || value.email}
              </Typography>
            </Box>
            {onClear ? (
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => {
                  onClear();
                  setQuery("");
                  setOpen(true);
                }}
              >
                Убрать
              </Button>
            ) : (
              <Chip color="success" startDecorator={<CheckCircleRounded />}>
                Выбран
              </Chip>
            )}
          </Stack>
        </Card>
      )}
      {open && (
        <div className="doc-search-results" aria-label="Результаты поиска">
          {error && <Alert color="danger">{error}</Alert>}
          {!loading &&
            !error &&
            !items.filter((x) => !exclude.includes(x.id)).length && (
              <Typography sx={{ p: 2 }} level="body-sm">
                Совпадений нет. Попробуйте другой запрос.
              </Typography>
            )}
          {items
            .filter((x) => !exclude.includes(x.id))
            .map((item) => (
              <button
                type="button"
                key={item.id}
                className="doc-search-item"
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
              >
                <span>
                  <strong>
                    {kind === "events"
                      ? item.address || `Заказ №${item.number}`
                      : nameOf(item)}
                  </strong>
                  <small>
                    {kind === "events"
                      ? `Сделка №${item.deal_id} · Событие №${item.id} · ${item.date || ""}`
                      : [item.phone, item.email, item.inn && `ИНН ${item.inn}`]
                          .filter(Boolean)
                          .join(" · ")}
                  </small>
                  {kind === "events" && (
                    <small>
                      {item.client ? nameOf(item.client) : "Заказчик не связан"}{" "}
                      · {item.state_display}
                    </small>
                  )}
                </span>
                {kind === "events" && <b>{money(item.amount)}</b>}
              </button>
            ))}
        </div>
      )}
    </Stack>
  );
}
const empty = {
  client_type: "individual",
  full_name: "",
  phone: "",
  email: "",
  company_name: "",
  inn: "",
  legal_address: "",
};
export function CustomerEditor({
  client,
  event,
  token,
  onSaved,
  onDirty,
  contact,
}) {
  const [form, setForm] = useState({
      ...empty,
      ...client,
      client_type: client?.client_type || "individual",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [advanced, setAdvanced] = useState(false);
  // Reset when the account changes; keep entered fields across normal parent renders.
  useEffect(() => {
    setForm({
      ...empty,
      ...client,
      client_type: client?.client_type || "individual",
      ...(!client && contact
        ? { full_name: contact.full_name || "", phone: contact.phone || "" }
        : {}),
    });
    setNotice("");
    setError("");
  }, [client?.id, contact?.phone, contact?.full_name]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setNotice("");
    onDirty?.();
  };
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await documentRequest("customers/save/", {
        token,
        method: "POST",
        data: {
          client_id: form.id || null,
          event_id: event?.id || null,
          values: form,
        },
      });
      setForm({ ...empty, ...r.client });
      setNotice(r.message);
      onSaved?.(r.client);
    } catch (e) {
      setError(e.message);
      if (e.body?.existing_client) {
        setForm({ ...empty, ...e.body.existing_client });
        onDirty?.();
      }
    } finally {
      setBusy(false);
    }
  };
  const business = form.client_type !== "individual";
  return (
    <Stack gap={2}>
      <Choice
        label="Тип заказчика"
        value={form.client_type}
        onChange={(v) => set("client_type", v)}
        options={CLIENT_TYPES}
      />
      <div className="doc-grid">
        <Field
          label="Телефон аккаунта"
          value={form.phone}
          type="tel"
          onChange={(v) => set("phone", v)}
          placeholder="+7 (999) 123-45-67"
          hint="По номеру находим существующий аккаунт; вход клиента остаётся по коду."
        />
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => set("email", v)}
        />
        {business && (
          <Field
            label="Наименование ИП / организации"
            value={form.company_name}
            onChange={(v) => set("company_name", v)}
            required
          />
        )}
        <Field
          label={business ? "ФИО контактного лица" : "ФИО заказчика"}
          value={form.full_name}
          onChange={(v) => set("full_name", v)}
          required={!business}
        />
        {business && (
          <Field
            label="ИНН"
            value={form.inn}
            inputMode="numeric"
            onChange={(v) => set("inn", v.replace(/\D/g, ""))}
          />
        )}
        {["ooo", "legal"].includes(form.client_type) && (
          <Field
            label="КПП"
            value={form.kpp}
            inputMode="numeric"
            onChange={(v) => set("kpp", v.replace(/\D/g, ""))}
          />
        )}
        <Field
          label={business ? "Юридический адрес" : "Адрес для документов"}
          value={form.legal_address}
          onChange={(v) => set("legal_address", v)}
        />
      </div>
      {business && <CompanyLookup form={form} setForm={setForm} token={token} ownerKey={`document-customer:${client?.id || contact?.phone || event?.id || 'new'}`} onDirty={onDirty} disabled={busy} />}
      <Button
        variant="plain"
        color="neutral"
        onClick={() => setAdvanced((v) => !v)}
        sx={{ alignSelf: "flex-start" }}
      >
        {advanced
          ? "Скрыть дополнительные реквизиты"
          : "Дополнительные реквизиты и подписант"}
      </Button>
      {advanced && (
        <div className="doc-grid">
          {Object.entries({
            ogrn: "ОГРН / ОГРНИП",
            postal_address: "Почтовый адрес",
            director_name: "ФИО подписанта",
            director_position: "Должность",
            signatory_basis: "Основание полномочий",
            bank_name: "Банк",
            bank_bik: "БИК",
            bank_account: "Расчётный счёт",
            bank_corr_account: "Корреспондентский счёт",
          }).map(([k, label]) => (
            <Field
              key={k}
              label={label}
              value={form[k]}
              onChange={(v) => set(k, v)}
            />
          ))}
        </div>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      <Button
        loading={busy}
        onClick={save}
        sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
      >
        {form.id ? "Сохранить реквизиты" : "Создать заказчика"}
        {event ? " и связать с объектом" : ""}
      </Button>
    </Stack>
  );
}
